import { Fragment, useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router';
import { useDispatch, useSelector } from 'react-redux';
import { TextField } from 'mui-rff';
import { Field, Form } from 'react-final-form';
import { FORM_ERROR } from 'final-form';
import moment from 'moment';
import {
  Alert,
  Autocomplete as MuiAutocomplete,
  Box,
  Button,
  CircularProgress,
  Chip,
  Collapse,
  Dialog,
  Divider,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  List,
  ListItem,
  ListItemText,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TextField as MuiTextField,
  Tooltip,
  Typography,
} from '@mui/material';
import styled from '@emotion/styled';
import {
  Add as AddIcon,
  Delete as DeleteIcon,
  PersonAdd as PersonAddIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  Downloading as DownloadingIcon,
} from '@mui/icons-material';

import MultiSelect from '@components/MultiSelect';
import ConfirmationDialog from '@components/ConfirmationDialog';
import { breadcrumbsActions } from '@store/breadcrumbs';
import { showSuccessMessage, showErrorMessage } from '@store/dashboard';

import {
  getUserManagementGroupList,
  getUserManagementGroupDetail,
  createGroup,
  updateGroup,
  deleteGroup,
  addUserToGroup as addUserToGroupAction,
  removeUserFromGroup as removeUserFromGroupAction,
  selectUserManagementGroupsList,
  selectIsLoadingUserManagementGroupsList,
  selectUserManagementGroupDetails,
  selectUserManagementGroupDetailStatuses,
  selectIsDeletingGroup,
  selectIsAddingUserToGroup,
  selectIsRemovingUserFromGroup,
} from '../../store/user-management-groups.slice';
import {
  getUserManagementRoleList,
  selectUserManagementRolesList,
  selectIsLoadingUserManagementRolesList,
} from '../../store/user-management-roles.slice';
import {
  getUserManagementUserList,
  selectUserManagementUsersList,
  selectIsLoadingUserManagementUsersList,
} from '../../store/user-management-users.slice';
import ApplicationPageTemplate from '../../../dashboard/components/ApplicationPageTemplate/ApplicationPageTemplate';
import { groupListBreadcrumb } from '../../user-management.breadcrumbs';
import { COLUMN_COUNT, MESSAGES } from '../../user-group.constants';

const MultiSelectLoadingContainer = styled(Stack)`
  flex-direction: row;
  gap: 4px;
  justify-content: space-between;
  border: 1px solid #c4c4c4;
  padding: 16px 14px;
  border-radius: 4px;
`;

const buildRoleObjects = (roleNames = [], availableRoles = []) =>
  roleNames.map((roleName) => {
    const roleFromList = availableRoles.find((r) => r.roleName === roleName);
    return roleFromList || { roleName };
  });

function Toolbar({ openNewGroupDialog, permissions }) {
  return (
    <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 2 }}>
      <Button
        variant="contained"
        disabled={!permissions?.canCreateUserManagementGroup}
        onClick={openNewGroupDialog}
        startIcon={<AddIcon />}>
        New Group
      </Button>
    </Box>
  );
}

export default function UserManagementGroupList({ permissions = {} }) {
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const groupList = useSelector(selectUserManagementGroupsList);
  const isLoadingGroupList = useSelector(selectIsLoadingUserManagementGroupsList);
  const groupDetails = useSelector(selectUserManagementGroupDetails);
  const groupDetailStatuses = useSelector(selectUserManagementGroupDetailStatuses);
  const isDeletingGroup = useSelector(selectIsDeletingGroup);
  const isAddingUser = useSelector(selectIsAddingUserToGroup);
  const isRemovingUser = useSelector(selectIsRemovingUserFromGroup);
  const roleList = useSelector(selectUserManagementRolesList);
  const isLoadingRoleList = useSelector(selectIsLoadingUserManagementRolesList);
  const userList = useSelector(selectUserManagementUsersList);
  const isLoadingUserList = useSelector(selectIsLoadingUserManagementUsersList);

  const [isNewGroupDialogOpen, setIsNewGroupDialogOpen] = useState(false);
  const [isUserDialogOpen, setIsUserDialogOpen] = useState(false);
  const [formValues, setFormValues] = useState({});
  const [isEditMode, setIsEditMode] = useState(false);
  const [selectedGroup, setSelectedGroup] = useState(null);
  const [expandedRows, setExpandedRows] = useState(new Set());
  const [selectedUser, setSelectedUser] = useState(null);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [showDeleteGroupModal, setShowDeleteGroupModal] = useState({
    show: false,
  });
  const [showRemoveUserModal, setShowRemoveUserModal] = useState({
    show: false,
  });
  const [userToRemove, setUserToRemove] = useState(null);

  useEffect(() => {
    if (!permissions?.canViewUserManagementGroup) {
      navigate('/landing');
    }
  }, [navigate, permissions]);

  // Breadcrumbs
  useEffect(() => {
    dispatch(breadcrumbsActions.showMenu(true));
    dispatch(breadcrumbsActions.setBreadcrumbs(groupListBreadcrumb));
  }, [dispatch]);

  // API: Get Group List and Role List
  useEffect(() => {
    dispatch(getUserManagementGroupList());
    dispatch(getUserManagementRoleList());
  }, [dispatch]);

  const resetForm = useCallback(() => {
    setIsNewGroupDialogOpen(false);
    setFormValues({});
    setIsEditMode(false);
  }, []);

  const resetUserDialog = useCallback(() => {
    setIsUserDialogOpen(false);
    setSelectedGroup(null);
    setSelectedUser(null);
  }, []);

  const onDeleteGroup = () => {
    setShowDeleteGroupModal({ show: true });
  };

  const onDeleteGroupConfirmation = () => {
    const { id } = formValues;

    const successMsg = MESSAGES.DeleteSuccess;
    const failureMsg = MESSAGES.DeleteFailure;

    dispatch(deleteGroup({ id })).then(({ payload: response }) => {
      if (
        !response ||
        (response.status !== 'OK' && response.status !== 'SUCCESS')
      ) {
        const message = response?.message || failureMsg;
        dispatch(showErrorMessage({ message, duration: 5000 }));

        return { [FORM_ERROR]: message };
      }

      dispatch(showSuccessMessage(successMsg));
      dispatch(getUserManagementGroupList());

      return resetForm();
    });
  };

  const handleSubmitGroup = (values) => {
    const { groupName, description, roles = [] } = values;
    const { id } = formValues;

    const selectedRoleNames = roles.map(({ roleName }) => roleName);

    const payload = {
      name: groupName,
      description: description || '',
      roles: selectedRoleNames,
    };

    let action = createGroup(payload);
    let successMsg = MESSAGES.CreateSuccess;
    let failureMsg = MESSAGES.CreateFailure;

    if (isEditMode) {
      action = updateGroup({ id, ...payload });
      successMsg = MESSAGES.UpdateSuccess;
      failureMsg = MESSAGES.UpdateFailure;
    }

    return dispatch(action).then(({ payload: response }) => {
      if (
        !response ||
        !['Created', 'OK', 'SUCCESS'].includes(response.status)
      ) {
        const message = response?.message || failureMsg;
        dispatch(showErrorMessage({ message, duration: 5000 }));

        return { [FORM_ERROR]: message };
      }

      dispatch(showSuccessMessage(successMsg));
      dispatch(getUserManagementGroupList());

      return resetForm();
    });
  };

  const handleAddUserToGroup = () => {
    if (!selectedUser) return;

    const payload = {
      userId: selectedUser.id,
    };

    dispatch(addUserToGroupAction({ groupId: selectedGroup.id, ...payload })).then(
      ({ payload: response }) => {
        if (
          !response ||
          (response.status !== 'OK' && response.status !== 'SUCCESS')
        ) {
          dispatch(
            showErrorMessage({
              message: MESSAGES.AddUserFailure,
              duration: 5000,
            }),
          );
          return;
        }

        dispatch(showSuccessMessage(MESSAGES.AddUserSuccess));
        dispatch(getUserManagementGroupList());
        dispatch(getUserManagementGroupDetail(selectedGroup.id));
        resetUserDialog();
      },
    );
  };

  const handleRemoveUserFromGroup = (groupId, userId) => {
    setUserToRemove({ groupId, userId });
    setShowRemoveUserModal({ show: true });
  };

  const onRemoveUserConfirmation = () => {
    if (!userToRemove) return;

    const { groupId, userId } = userToRemove;

    dispatch(removeUserFromGroupAction({ groupId, userId })).then(
      ({ payload: response }) => {
        if (
          !response ||
          (response.status !== 'OK' && response.status !== 'SUCCESS')
        ) {
          dispatch(
            showErrorMessage({
              message: MESSAGES.RemoveUserFailure,
              duration: 5000,
            }),
          );
          setUserToRemove(null);
          return;
        }

        dispatch(showSuccessMessage(MESSAGES.RemoveUserSuccess));
        dispatch(getUserManagementGroupList());
        dispatch(getUserManagementGroupDetail(groupId));
        setSelectedGroup(null);
        setUserToRemove(null);
      },
    );
  };

  const validateNewGroup = (values) => {
    const errors = {};

    if (!values.groupName) {
      errors.groupName = MESSAGES.FormFieldRequiredError;
    } else {
      const isGroupNameLengthValid = values.groupName.length > 4;
      if (!isGroupNameLengthValid) {
        errors.groupName = MESSAGES.FormFieldGroupNameLengthError;
      }
    }

    return errors;
  };

  const toggleRowExpansion = (groupId) => {
    if (expandedRows.has(groupId)) {
      setExpandedRows(new Set());
    } else {
      setExpandedRows(new Set([groupId]));
      dispatch(getUserManagementGroupDetail(groupId));
    }
  };

  const openNewGroupDialog = () => {
    setIsNewGroupDialogOpen(true);
  };

  const onRowClick = (group) => {
    dispatch(getUserManagementGroupDetail(group.id))
      .unwrap()
      .then((detail) => {
        setFormValues({
          id: detail.id,
          groupName: detail.name,
          description: detail.description || '',
          roles: buildRoleObjects(detail.roles, roleList),
          members: detail.members || [],
        });
        setIsEditMode(true);
        setIsNewGroupDialogOpen(true);
      })
      .catch(() => {
        dispatch(
          showErrorMessage({
            message: MESSAGES.LoadDetailFailure,
            duration: 5000,
          }),
        );
      });
  };

  return (
    <ApplicationPageTemplate
      title="User Management - Groups"
      description="Manage groups and assign users with roles"
      pageReady>
      <Toolbar
        openNewGroupDialog={openNewGroupDialog}
        permissions={permissions}
      />

      {isLoadingGroupList ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
          <CircularProgress />
        </Box>
      ) : (
        <TableContainer component={Paper}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell width={50} />
                <TableCell align="center">ID</TableCell>
                <TableCell>Group Name</TableCell>
                <TableCell align="center">Members</TableCell>
                <TableCell align="center">Created By</TableCell>
                <TableCell align="center">Modified By</TableCell>
                <TableCell align="right">Last Modified Date</TableCell>
                <TableCell align="center">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {(groupList || [])
                .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                .map((group) => {
                  const detail = groupDetails[group.id];
                  const isLoadingDetail =
                    groupDetailStatuses[group.id] === 'loading';
                  const members = detail?.members || [];
                  return (
                  <Fragment key={group.id}>
                    <TableRow
                      hover
                      sx={{
                        cursor: 'pointer',
                        '& > *': {
                          borderBottom: expandedRows.has(group.id)
                            ? 'unset'
                            : undefined,
                        },
                      }}
                      onClick={() => onRowClick(group)}>
                      <TableCell>
                        <IconButton
                          size="small"
                          onClick={(e) => {
                            e.stopPropagation();
                            toggleRowExpansion(group.id);
                          }}>
                          {expandedRows.has(group.id) ? (
                            <ExpandLessIcon />
                          ) : (
                            <ExpandMoreIcon />
                          )}
                        </IconButton>
                      </TableCell>
                      <TableCell align="center">{group.id}</TableCell>
                      <TableCell>{group.name}</TableCell>
                      <TableCell align="center">
                        {group.memberCount || 0}
                      </TableCell>
                      <TableCell align="center">{group.createdBy}</TableCell>
                      <TableCell align="center">{group.modifiedBy}</TableCell>
                      <TableCell align="right">
                        {moment(group.modifiedAt).format('YYYY/MM/DD - HH:mm')}
                      </TableCell>
                      <TableCell align="center">
                        <Button
                          variant="outlined"
                          size="small"
                          startIcon={<PersonAddIcon />}
                          onClick={(e) => {
                            e.stopPropagation();
                            setSelectedGroup(group);
                            dispatch(getUserManagementUserList());
                            setIsUserDialogOpen(true);
                          }}
                          disabled={!permissions?.canUpdateUserManagementGroup}>
                          Add User
                        </Button>
                      </TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell
                        sx={{ py: 0, border: 0 }}
                        colSpan={COLUMN_COUNT}>
                        <Collapse
                          in={expandedRows.has(group.id)}
                          timeout="auto"
                          unmountOnExit>
                          <Box
                            sx={{
                              m: 2,
                              border: '1px solid',
                              borderColor: 'divider',
                              borderRadius: 1,
                              bgcolor: 'grey.50',
                            }}>
                            <Box
                              sx={{
                                px: 2,
                                py: 1.5,
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'space-between',
                                borderBottom: '1px solid',
                                borderColor: 'divider',
                                bgcolor: 'grey.100',
                                borderRadius: '4px 4px 0 0',
                              }}>
                              <Typography
                                variant="subtitle2"
                                fontWeight={600}>
                                Members
                              </Typography>
                              <Chip
                                label={`${
                                  detail ? members.length : group.memberCount || 0
                                } user${
                                  (detail ? members.length : group.memberCount) !==
                                  1
                                    ? 's'
                                    : ''
                                }`}
                                size="small"
                                color="primary"
                                variant="outlined"
                              />
                            </Box>
                            {(() => {
                              if (isLoadingDetail) {
                                return (
                                  <Box
                                    sx={{
                                      p: 2,
                                      display: 'flex',
                                      justifyContent: 'center',
                                    }}>
                                    <CircularProgress size={20} />
                                  </Box>
                                );
                              }
                              if (members.length === 0) {
                                return (
                                  <Box sx={{ p: 2 }}>
                                    <Typography
                                      variant="body2"
                                      color="text.secondary">
                                      No users in this group yet
                                    </Typography>
                                  </Box>
                                );
                              }
                              return (
                                <List
                                  dense
                                  disablePadding>
                                  {members.map((member, index) => (
                                    <Fragment key={member.userId}>
                                      {index > 0 && <Divider />}
                                      <ListItem
                                        sx={{ px: 2, py: 1 }}
                                        secondaryAction={
                                          <IconButton
                                            edge="end"
                                            size="small"
                                            onClick={() =>
                                              handleRemoveUserFromGroup(
                                                group.id,
                                                member.userId,
                                              )
                                            }
                                            disabled={
                                              !permissions?.canUpdateUserManagementGroup ||
                                              isRemovingUser
                                            }>
                                            <DeleteIcon fontSize="small" />
                                          </IconButton>
                                        }>
                                        <ListItemText
                                          primary={
                                            <Typography
                                              variant="body2"
                                              fontWeight={500}>
                                              {member.username || 'Unknown User'}
                                            </Typography>
                                          }
                                          secondary={member.email || 'No email'}
                                        />
                                      </ListItem>
                                    </Fragment>
                                  ))}
                                </List>
                              );
                            })()}
                          </Box>

                          {/* Roles Section */}
                          <Box
                            sx={{
                              m: 2,
                              mt: 0,
                              border: '1px solid',
                              borderColor: 'divider',
                              borderRadius: 1,
                              bgcolor: 'grey.50',
                            }}>
                            <Box
                              sx={{
                                px: 2,
                                py: 1.5,
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'space-between',
                                borderBottom: '1px solid',
                                borderColor: 'divider',
                                bgcolor: 'grey.100',
                                borderRadius: '4px 4px 0 0',
                              }}>
                              <Typography
                                variant="subtitle2"
                                fontWeight={600}>
                                Roles
                              </Typography>
                              <Chip
                                label={`${group.roles?.length || 0} role${
                                  group.roles?.length !== 1 ? 's' : ''
                                }`}
                                size="small"
                                color="secondary"
                                variant="outlined"
                              />
                            </Box>
                            {group.roles && group.roles.length > 0 ? (
                              <Box
                                sx={{
                                  p: 2,
                                  display: 'flex',
                                  flexWrap: 'wrap',
                                  gap: 1,
                                }}>
                                {group.roles.map((role) => (
                                  <Chip
                                    key={role}
                                    label={role}
                                    size="small"
                                    variant="outlined"
                                  />
                                ))}
                              </Box>
                            ) : (
                              <Box sx={{ p: 2 }}>
                                <Typography
                                  variant="body2"
                                  color="text.secondary">
                                  No roles assigned to this group yet
                                </Typography>
                              </Box>
                            )}
                          </Box>
                        </Collapse>
                      </TableCell>
                    </TableRow>
                  </Fragment>
                  );
                })}
            </TableBody>
          </Table>
          <TablePagination
            rowsPerPageOptions={[10, 25]}
            component="div"
            count={(groupList || []).length}
            rowsPerPage={rowsPerPage}
            page={page}
            onPageChange={(_, newPage) => setPage(newPage)}
            onRowsPerPageChange={(e) => {
              setRowsPerPage(parseInt(e.target.value, 10));
              setPage(0);
            }}
          />
        </TableContainer>
      )}

      {/* Group Create/Edit Dialog */}
      <Dialog
        fullWidth
        open={isNewGroupDialogOpen}>
        <DialogTitle>
          {isEditMode ? 'Update Existing Group' : 'Add New Group'}
        </DialogTitle>
        <Form
          onSubmit={handleSubmitGroup}
          initialValues={isEditMode ? formValues : {}}
          validate={validateNewGroup}
          render={({ submitError, handleSubmit, submitting }) => (
            <form
              onSubmit={handleSubmit}
              noValidate>
              <DialogContent style={{ minHeight: '280px' }}>
                <Box sx={{ marginTop: '10px', marginBottom: '20px' }}>
                  <TextField
                    label="Group Name"
                    name="groupName"
                    required
                    fullWidth
                  />
                </Box>
                <Box sx={{ marginBottom: '20px' }}>
                  <TextField
                    label="Description"
                    name="description"
                    fullWidth
                    multiline
                    rows={3}
                  />
                </Box>
                <Box sx={{ marginBottom: '10px' }}>
                  {isLoadingRoleList ? (
                    <MultiSelectLoadingContainer>
                      <Typography color="#666666">Select roles</Typography>
                      <Tooltip title="Loading list of roles...">
                        <DownloadingIcon />
                      </Tooltip>
                    </MultiSelectLoadingContainer>
                  ) : (
                    <Field name="roles">
                      {({ input: { name, value, onChange } }) => (
                        <MultiSelect
                          id="roleOptions"
                          {...{ name, value, onChange }}
                          items={roleList}
                          isOptionEqualToValue={(option, val) =>
                            option.roleName === val.roleName
                          }
                          label="Assign Roles to Group"
                          multiple
                          optionDisplayKey="roleName"
                        />
                      )}
                    </Field>
                  )}
                </Box>

                {submitError && (
                  <Alert
                    severity="error"
                    style={{ marginTop: 30 }}>
                    {submitError}
                  </Alert>
                )}
              </DialogContent>
              <DialogActions
                sx={{
                  justifyContent: isEditMode ? 'space-between' : 'flex-end',
                }}>
                {isEditMode && (
                  <Button
                    variant="outlined"
                    onClick={onDeleteGroup}
                    startIcon={<DeleteIcon />}
                    disabled={
                      !permissions?.canDeleteUserManagementGroup || isDeletingGroup
                    }>
                    Delete Group
                  </Button>
                )}
                <Box>
                  <Button
                    variant="text"
                    onClick={resetForm}>
                    Cancel
                  </Button>
                  <Button
                    variant="contained"
                    type="submit"
                    disabled={
                      (isEditMode
                        ? !permissions?.canUpdateUserManagementGroup
                        : !permissions?.canCreateUserManagementGroup) ||
                      submitting
                    }>
                    Submit
                  </Button>
                </Box>
              </DialogActions>
            </form>
          )}
        />
      </Dialog>

      {/* Add User to Group Dialog */}
      <Dialog
        fullWidth
        open={isUserDialogOpen}>
        <DialogTitle>Add User to {selectedGroup?.name}</DialogTitle>
        <DialogContent>
          <Box sx={{ mt: 1 }}>
            <MuiAutocomplete
              options={userList}
              loading={isLoadingUserList}
              value={selectedUser}
              onChange={(_, newValue) => setSelectedUser(newValue)}
              getOptionLabel={(option) => option.email || ''}
              renderOption={(props, option) => (
                <li
                  {...props}
                  key={option.id}>
                  <ListItemText
                    primary={option.email}
                    secondary={option.username}
                  />
                </li>
              )}
              isOptionEqualToValue={(option, value) => option.id === value.id}
              renderInput={(params) => (
                <MuiTextField
                  {...params}
                  label="Select User"
                  required
                  InputProps={{
                    ...params.InputProps,
                    endAdornment: (
                      <>
                        {isLoadingUserList ? (
                          <CircularProgress size={20} />
                        ) : null}
                        {params.InputProps.endAdornment}
                      </>
                    ),
                  }}
                />
              )}
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button
            variant="text"
            onClick={resetUserDialog}>
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleAddUserToGroup}
            disabled={
              !selectedUser ||
              !permissions?.canUpdateUserManagementGroup ||
              isAddingUser
            }>
            Add User
          </Button>
        </DialogActions>
      </Dialog>

      <ConfirmationDialog
        openDialog={showDeleteGroupModal}
        yesHandler={onDeleteGroupConfirmation}
        title={MESSAGES.DeleteGroupDialogTitle}
        message={MESSAGES.DeleteGroupDialogDescription}
      />

      <ConfirmationDialog
        openDialog={showRemoveUserModal}
        yesHandler={onRemoveUserConfirmation}
        title={MESSAGES.RemoveUserDialogTitle}
        message={MESSAGES.RemoveUserDialogDescription}
      />
    </ApplicationPageTemplate>
  );
}
